import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { DatePicker, Select, Button, Tag, Card, Tabs, Checkbox, Input, Modal, Drawer, Form, InputNumber, Empty } from "antd";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import dayjs from "dayjs";
import {
    GetCarteraAseguradoras,
    GetCarteraPorAseguradora,
    GetCarteraAseguradorasExcel,
    GetCarteraAseguradorasPdf,
    RegistrarCobroAseguradora,
    ListarCobrosAseguradora,
    ReversarCobroAseguradora,
} from "../../Service/Service.reporteCartera";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../../Service/Services.seguros";
import { ListarMetoPago } from "../../Service/Service.util";
import usePermissions from "../../hook/usePermissions";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble } from "../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../Components/UI/Chip";
import BotonKit from "../../Components/UI/Boton";
import MenuAcciones from "../../Components/UI/MenuAcciones";
import BotonesExportar from "../../Components/UI/BotonesExportar";
import { useToast } from "../../Components/UI/Aviso";
import { IconoBuscar, IconoHistorial, IconoRevertir } from "../../Components/UI/Iconos";
import { usePageSubtitulo } from "../../context/PageHeaderContext";

const { RangePicker } = DatePicker;

// COBERTURAS-07 -- Cartera por Aseguradora. Vista ANALÍTICA (grano = 1 FAC a
// aseguradora, NUNCA 1 PCP). Filtros SIEMPRE VISIBLES (misma regla UX de
// 06/09/04). NO se muestra "Vencido" -- el modelo no tiene fecha_vencimiento
// real; solo se informa "días desde emisión".
//
// COBERTURAS-07B -- añade el registro real de cobros contra el ledger
// `cobros_aseguradora` (antes el reporte era 100% solo-lectura, ver
// context/22 §B). El backend SIEMPRE recalcula saldo/estado server-side;
// esta vista nunca confía un saldo calculado en el cliente, solo lo muestra
// y refresca inmediatamente después de registrar/reversar un cobro.
const ESTADO_CARTERA = [
    { value: "PENDIENTE", label: "Pendiente" },
    { value: "PARCIAL", label: "Parcial" },
    { value: "COBRADO", label: "Cobrado" },
    { value: "ANULADO", label: "Anulado" },
];
// SISTEMA-DE-DISENO: el color pasa a significar lo mismo que en el resto del
// sistema. "Parcial" era AZUL, que aqui es accion, no estado.
const TONO_CARTERA = { PENDIENTE: "pendiente", PARCIAL: "proceso", COBRADO: "hecho", ANULADO: "anulado" };
const CONTIFICO_LABEL = { PENDING: "Pendiente de envío", PROCESSING: "Enviando...", CREATED: "Enviado", FAILED: "Error de envío" };
const EMPTY = { rangoFechas: null, aseguradora: undefined, estado_cartera: undefined, estado_contifico: undefined, num_documento: undefined, incluir_anuladas: false };

// Un solo sitio para volcar el blob que devuelve el backend.
const descargarBlob = (respuesta, nombre) => {
    const url = URL.createObjectURL(respuesta.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
};

function ReporteCartera() {
    const [tab, setTab] = useState("factura");
    const [draft, setDraft] = useState(EMPTY);
    const [applied, setApplied] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ facturas: 0, facturado: 0, cobrado: 0, saldo: 0, pendientes: 0 });

    const [resumenRows, setResumenRows] = useState([]);
    const [resumenLoading, setResumenLoading] = useState(false);
    const [resumenError, setResumenError] = useState(null);

    const [seguros, setSeguros] = useState([]);
    const [metodosPago, setMetodosPago] = useState([]);

    // COBERTURAS-07B -- modal "Registrar cobro" + drawer "Historial de cobros".
    const { hasPermission } = usePermissions();
    const [cobroModal, setCobroModal] = useState(null); // fila de factura seleccionada
    const [cobroForm] = Form.useForm();
    const [cobroSaving, setCobroSaving] = useState(false);
    const [historial, setHistorial] = useState(null); // { factura, cobros, summary }
    const [historialLoading, setHistorialLoading] = useState(false);
    const [reversando, setReversando] = useState(null); // id del cobro en curso
    const { mostrar } = useToast();

    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const seg = await ControllerListarSegurosClinica(idclinica);
            setSeguros((seg || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
            // Fallback inicial con la clínica del usuario logueado -- se
            // sobreescribe con la clínica REAL de la factura en
            // abrirRegistrarCobro (COBERTURAS-07B1, R2: un admin multi-clínica
            // puede cobrar una FAC de una clínica distinta a la suya).
            const met = await ListarMetoPago();
            setMetodosPago((met?.data || []).map((m) => ({ value: m.idmetodo_pago, label: m.metodo })));
        })();
    }, []);

    const fetchDetalle = useCallback(async (p = 1, ps = 20, filters = {}) => {
        setLoading(true);
        setError(null);
        const res = await GetCarteraAseguradoras({ page: p, limit: ps, filters });
        if (res?.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idfactura })));
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.limit || ps);
            setSummary(res.summary || { facturas: 0, facturado: 0, cobrado: 0, saldo: 0, pendientes: 0 });
        } else {
            // ESTADO DE ERROR: antes la tabla se vaciaba en silencio, y era
            // indistinguible de "no hay cartera pendiente".
            setRows([]);
            setTotal(0);
            setError(res?.message || "No se pudo obtener la cartera por aseguradora.");
        }
        setLoading(false);
    }, []);

    const fetchResumen = useCallback(async (filters = {}) => {
        setResumenLoading(true);
        setResumenError(null);
        const res = await GetCarteraPorAseguradora({ filters });
        setResumenRows(res?.success ? (res.data || []).map((r) => ({ ...r, key: r.idseguro })) : []);
        if (!res?.success) setResumenError(res?.message || "No se pudo obtener el resumen por aseguradora.");
        setResumenLoading(false);
    }, []);

    const refrescarTodo = useCallback(() => {
        fetchDetalle(page, pageSize, applied);
        fetchResumen(applied);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, applied]);

    const abrirRegistrarCobro = async (row) => {
        setCobroModal(row);
        cobroForm.setFieldsValue({
            valor: row.saldo,
            fecha_cobro: dayjs(),
            metodo_pago_id: undefined,
            referencia: undefined,
            observacion: undefined,
        });
        // COBERTURAS-07B1 (R2): los métodos de pago deben ser los de la
        // clínica DUEÑA de la factura (row.idclinica, viene de
        // FReportCartera.ts), NUNCA los de la clínica del usuario logueado --
        // un admin multi-clínica puede estar cobrando una FAC de otra de sus
        // clínicas autorizadas. El backend igual re-valida el método contra
        // la clínica real de la factura en RegistrarCobro (07B, sin cambios).
        if (row?.idclinica) {
            const met = await ListarMetoPago(row.idclinica);
            setMetodosPago((met?.data || []).map((m) => ({ value: m.idmetodo_pago, label: m.metodo })));
        }
    };

    const confirmarCobro = async () => {
        try {
            const values = await cobroForm.validateFields();
            setCobroSaving(true);
            const res = await RegistrarCobroAseguradora({
                idfactura: cobroModal.idfactura,
                valor: values.valor,
                fecha_cobro: values.fecha_cobro.format("YYYY-MM-DD"),
                metodo_pago_id: values.metodo_pago_id,
                referencia: values.referencia,
                observacion: values.observacion,
            });
            setCobroSaving(false);
            if (res.ok) {
                mostrar({ texto: res.message || "Cobro registrado" });
                setCobroModal(null);
                refrescarTodo();
                if (historial?.factura?.idfactura === cobroModal.idfactura) {
                    abrirHistorial({ idfactura: cobroModal.idfactura, num_documento: cobroModal.num_documento });
                }
            } else {
                mostrar({ tipo: "error", texto: res.message || "No se pudo registrar el cobro." });
            }
        } catch (e) {
            setCobroSaving(false);
            // errores de validación del formulario -- no hacer nada, antd ya los muestra
        }
    };

    const abrirHistorial = async (row) => {
        setHistorial({ factura: row, cobros: [], summary: null });
        setHistorialLoading(true);
        const res = await ListarCobrosAseguradora(row.idfactura);
        setHistorial({
            factura: row,
            cobros: res?.success ? res.data || [] : [],
            summary: res?.summary || null,
        });
        setHistorialLoading(false);
    };

    const reversarCobro = async (cobro) => {
        const { value: motivo } = await Swal.fire({
            title: "Reversar cobro",
            text: `¿Reversar el cobro de $${Number(cobro.valor).toFixed(2)}? Esta acción no elimina el registro, solo lo marca como reversado.`,
            input: "text",
            inputLabel: "Motivo (obligatorio)",
            inputValidator: (v) => (!v || v.trim().length < 3 ? "El motivo debe tener al menos 3 caracteres" : null),
            showCancelButton: true,
            confirmButtonText: "Reversar",
            cancelButtonText: "Cancelar",
        });
        if (!motivo) return;
        setReversando(cobro.id);
        const res = await ReversarCobroAseguradora({ id: cobro.id, motivo });
        setReversando(null);
        if (res.ok) {
            mostrar({ texto: "Cobro reversado" });
            refrescarTodo();
            if (historial?.factura?.idfactura) {
                abrirHistorial(historial.factura);
            }
        } else {
            mostrar({ tipo: "error", texto: res.message || "No se pudo reversar el cobro." });
        }
    };

    const aplicar = () => {
        const [d0, d1] = draft.rangoFechas || [];
        const f = {
            fecha_inicial: d0 ? d0.format("YYYY-MM-DD") : undefined,
            fecha_final: d1 ? d1.format("YYYY-MM-DD") : undefined,
            aseguradora: draft.aseguradora,
            estado_cartera: draft.estado_cartera,
            estado_contifico: draft.estado_contifico,
            num_documento: draft.num_documento,
            incluir_anuladas: draft.incluir_anuladas,
        };
        setApplied(f);
        fetchDetalle(1, pageSize, f);
        fetchResumen(f);
    };
    const limpiar = () => {
        setDraft(EMPTY);
        setApplied({});
        fetchDetalle(1, pageSize, {});
        fetchResumen({});
    };

    useEffect(() => {
        fetchDetalle(1, pageSize, {});
        fetchResumen({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

    const columnas = [
        {
            // Numero de factura arriba; fecha y clinica, debajo.
            key: "num_documento",
            titulo: "Factura",
            ancho: 210,
            render: (r) => (
                <CeldaDoble
                    principal={r.num_documento}
                    secundario={[
                        r.fecha_emision ? format(new Date(r.fecha_emision), "dd/MM/yyyy", { locale: es }) : null,
                        r.clinica,
                    ]
                        .filter(Boolean)
                        .join(" · ")}
                    monoPrincipal
                />
            ),
        },
        {
            key: "aseguradora",
            titulo: "Aseguradora",
            render: (r) => <CeldaDoble principal={r.aseguradora} secundario={r.ruc} monoSecundario />,
        },
        {
            // En cartera lo que importa es el SALDO: manda el, y debajo va de
            // donde sale.
            key: "saldo",
            titulo: "Saldo",
            ancho: 200,
            alinear: "derecha",
            render: (r) => (
                <CeldaDoble
                    principal={money(r.saldo)}
                    secundario={`Fact ${money(r.total)} · Cob ${money(r.cobrado)}`}
                    monoPrincipal
                />
            ),
        },
        {
            key: "estado_cartera",
            titulo: "Estado",
            ancho: 170,
            render: (r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <ChipEstado tono={TONO_CARTERA[r.estado_cartera] || "pendiente"}>
                        {ESTADO_CARTERA.find((x) => x.value === r.estado_cartera)?.label || r.estado_cartera}
                    </ChipEstado>
                    <span className="dt-doble__secundario">
                        {r.dias_desde_emision == null ? "—" : `${r.dias_desde_emision} días desde emisión`}
                    </span>
                </div>
            ),
        },
        {
            // Envio electronico: Contifico arriba, SRI debajo. Un envio con
            // error se marca como dato incompleto, no como un estado mas.
            key: "estado_contifico",
            titulo: "Envío",
            ancho: 180,
            render: (r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <ChipCategoria
                        tono={r.estado_contifico === "CREATED" ? "info" : undefined}
                        incompleto={r.estado_contifico === "FAILED" || !r.estado_contifico}
                    >
                        {CONTIFICO_LABEL[r.estado_contifico] || "Pendiente de envío"}
                    </ChipCategoria>
                    <span className="dt-doble__secundario">{r.sri_status ? `SRI: ${r.sri_status}` : "SRI: —"}</span>
                </div>
            ),
        },
        {
            key: "acciones",
            titulo: "",
            ancho: 160,
            alinear: "derecha",
            render: (row) => {
                const puedeCobrar =
                    row.estado_cartera !== "ANULADO" && row.saldo > 0 && hasPermission("billing.collections");
                return (
                    <span className="ac-celda-accion">
                        {puedeCobrar && (
                            <BotonKit
                                tamano="sm"
                                variante="primario"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    abrirRegistrarCobro(row);
                                }}
                                data-testid={`cartera-cobrar-${row.idfactura}`}
                            >
                                Cobrar
                            </BotonKit>
                        )}
                        <MenuAcciones
                            titulo={`Más acciones de la factura ${row.num_documento}`}
                            acciones={[
                                {
                                    key: "historial",
                                    label: "Historial de cobros",
                                    icono: IconoHistorial,
                                    onClick: () => abrirHistorial(row),
                                },
                            ]}
                        />
                    </span>
                );
            },
        },
    ];

    const columnasResumen = [
        {
            key: "aseguradora",
            titulo: "Aseguradora",
            render: (r) => <CeldaDoble principal={r.aseguradora} secundario={r.ruc} monoSecundario />,
        },
        {
            key: "facturas",
            titulo: "Facturas",
            ancho: 170,
            alinear: "derecha",
            render: (r) => (
                <CeldaDoble
                    principal={r.facturas}
                    secundario={r.pendientes ? `${r.pendientes} pendientes` : "sin pendientes"}
                    monoPrincipal
                />
            ),
        },
        { key: "facturado", titulo: "Facturado", ancho: 150, alinear: "derecha", render: (r) => <Celda valor={money(r.facturado)} mono /> },
        { key: "cobrado", titulo: "Cobrado", ancho: 150, alinear: "derecha", render: (r) => <Celda valor={money(r.cobrado)} mono /> },
        { key: "saldo", titulo: "Saldo", ancho: 150, alinear: "derecha", render: (r) => <Celda valor={money(r.saldo)} mono /> },
    ];

    const hayFiltros = Object.values(applied).some((v) => v !== undefined && v !== null && v !== "" && v !== false);

    usePageSubtitulo(loading ? "" : `${total} factura${total === 1 ? "" : "s"} · saldo ${money(summary.saldo)}`);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Row className="mb-2">
                    <Col>
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item">Reportes</li>
                            <li className="breadcrumb-item active">Cartera por Aseguradora</li>
                        </ol>
                    </Col>
                </Row>

                {/* ---- FILTROS SIEMPRE VISIBLES ---- */}
                <Row className="g-2 align-items-end mb-3 p-2 border rounded bg-light">
                    <Col xs={12} md={4} lg={3}>
                        <label className="kc__etiqueta"><small>Rango de fechas</small></label>
                        <RangePicker className="w-100" value={draft.rangoFechas} onChange={(d) => setDraft((f) => ({ ...f, rangoFechas: d }))} getPopupContainer={() => document.body} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Aseguradora</small></label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={seguros} value={draft.aseguradora} onChange={(v) => setDraft((f) => ({ ...f, aseguradora: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Estado cartera</small></label>
                        <Select className="w-100" allowClear options={ESTADO_CARTERA} value={draft.estado_cartera} onChange={(v) => setDraft((f) => ({ ...f, estado_cartera: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Estado Contífico</small></label>
                        <Select
                            className="w-100" allowClear
                            options={[
                                { value: "PENDING", label: "Pendiente de envío" },
                                { value: "PROCESSING", label: "Enviando..." },
                                { value: "CREATED", label: "Enviado" },
                                { value: "FAILED", label: "Error de envío" },
                            ]}
                            value={draft.estado_contifico}
                            onChange={(v) => setDraft((f) => ({ ...f, estado_contifico: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>N° de factura</small></label>
                        <Input
                            className="w-100" allowClear placeholder="Buscar..."
                            value={draft.num_documento || ""}
                            onChange={(e) => setDraft((f) => ({ ...f, num_documento: e.target.value || undefined }))}
                        />
                    </Col>
                    <Col xs={12} md={4} lg={2} className="d-flex align-items-center">
                        <Checkbox checked={draft.incluir_anuladas} onChange={(e) => setDraft((f) => ({ ...f, incluir_anuladas: e.target.checked }))}>
                            Incluir anuladas
                        </Checkbox>
                    </Col>
                    <Col xs={12} lg={12} className="d-flex gap-2 mt-2 flex-wrap">
                        <BotonKit variante="primario" icono={IconoBuscar} onClick={aplicar} data-testid="cartera-buscar">
                            Buscar
                        </BotonKit>
                        <BotonKit onClick={limpiar} data-testid="cartera-limpiar">
                            Limpiar
                        </BotonKit>
                        {/* Ambos archivos los genera el backend; el permiso es
                            barrera real en esas rutas. */}
                        <BotonesExportar
                            permiso="reportes.export"
                            titulo="Cartera por Aseguradora"
                            idPrefijo="cartera"
                            onExcel={async () => {
                                try {
                                    const r = await GetCarteraAseguradorasExcel({ filters: applied });
                                    descargarBlob(r, "CARTERA_ASEGURADORAS.xlsx");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el Excel." });
                                }
                            }}
                            onPdf={async () => {
                                try {
                                    const r = await GetCarteraAseguradorasPdf({ filters: applied });
                                    descargarBlob(r, "CARTERA_ASEGURADORAS.pdf");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el PDF." });
                                }
                            }}
                        />
                    </Col>
                </Row>

                <div className="ac-filtros-linea mb-3" data-testid="cartera-totales">
                    {[
                        { etiqueta: "Facturado", valor: money(summary.facturado) },
                        { etiqueta: "Cobrado", valor: money(summary.cobrado) },
                        { etiqueta: "Saldo pendiente", valor: money(summary.saldo) },
                        { etiqueta: "Facturas pendientes", valor: summary.pendientes },
                    ].map((t) => (
                        <div className="ac-panel" key={t.etiqueta} style={{ flex: "1 1 180px", padding: "10px 14px", gap: 2 }}>
                            <span className="ac-etiqueta">{t.etiqueta}</span>
                            <span style={{ fontSize: 18, fontWeight: 600 }}>{t.valor}</span>
                        </div>
                    ))}
                </div>

                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        {
                            key: "factura",
                            label: "Por factura",
                            children: (
                                <DataTable
                                    columnas={columnas}
                                    filas={rows}
                                    filaKey="idfactura"
                                    cargando={loading}
                                    error={error}
                                    onReintentar={() => fetchDetalle(page, pageSize, applied)}
                                    etiquetaTabla="Cartera por factura"
                                    hayFiltrosAplicados={hayFiltros}
                                    onLimpiarFiltros={limpiar}
                                    textoVacioSinFiltros="No hay facturas a aseguradoras todavía."
                                    paginacion={{
                                        page,
                                        pageSize,
                                        total,
                                        tamanos: [20, 50, 100],
                                        onCambio: (p, ps) => fetchDetalle(p, ps, applied),
                                    }}
                                />
                            ),
                        },
                        {
                            key: "aseguradora",
                            label: "Por aseguradora",
                            children: (
                                <DataTable
                                    columnas={columnasResumen}
                                    filas={resumenRows}
                                    filaKey="idseguro"
                                    cargando={resumenLoading}
                                    error={resumenError}
                                    onReintentar={() => fetchResumen(applied)}
                                    etiquetaTabla="Cartera agrupada por aseguradora"
                                    hayFiltrosAplicados={hayFiltros}
                                    onLimpiarFiltros={limpiar}
                                    textoVacioSinFiltros="No hay cartera registrada todavía."
                                />
                            ),
                        },
                    ]}
                />
            </div>

            {/* COBERTURAS-07B -- Registrar cobro. El backend SIEMPRE recalcula
                el saldo dentro de la transacción (nunca se confía el valor
                mostrado aquí); el campo "valor" solo se prellena con el
                saldo para agilizar un cobro total, pero es editable para un
                cobro parcial. */}
            <Modal
                title={cobroModal ? `Registrar cobro — FAC ${cobroModal.num_documento}` : "Registrar cobro"}
                open={!!cobroModal}
                onCancel={() => setCobroModal(null)}
                onOk={confirmarCobro}
                confirmLoading={cobroSaving}
                okText="Registrar cobro"
                cancelText="Cancelar"
                destroyOnClose
            >
                {cobroModal && (
                    <>
                        <Row className="mb-3 g-2">
                            <Col xs={6}><small className="text-muted">Aseguradora</small><div>{cobroModal.aseguradora || "—"}</div></Col>
                            <Col xs={6}><small className="text-muted">Total</small><div>{money(cobroModal.total)}</div></Col>
                            <Col xs={6}><small className="text-muted">Cobrado</small><div>{money(cobroModal.cobrado)}</div></Col>
                            <Col xs={6}><small className="text-muted">Saldo pendiente</small><div><b>{money(cobroModal.saldo)}</b></div></Col>
                        </Row>
                        <Form form={cobroForm} layout="vertical">
                            <Form.Item
                                name="valor" label="Valor a cobrar"
                                rules={[
                                    { required: true, message: "Ingrese el valor" },
                                    { type: "number", min: 0.01, message: "El valor debe ser mayor a cero" },
                                ]}
                            >
                                <InputNumber className="w-100" min={0.01} max={Number(cobroModal.saldo)} step={0.01} precision={2} prefix="$" />
                            </Form.Item>
                            <Form.Item name="fecha_cobro" label="Fecha del cobro" rules={[{ required: true, message: "Ingrese la fecha" }]}>
                                <DatePicker className="w-100" getPopupContainer={() => document.body} />
                            </Form.Item>
                            <Form.Item name="metodo_pago_id" label="Método de pago" rules={[{ required: true, message: "Seleccione el método de pago" }]}>
                                <Select options={metodosPago} placeholder="Seleccione..." getPopupContainer={() => document.body} />
                            </Form.Item>
                            <Form.Item name="referencia" label="Referencia (N° transacción, cheque, comprobante)">
                                <Input maxLength={250} />
                            </Form.Item>
                            <Form.Item name="observacion" label="Observación">
                                <Input.TextArea maxLength={500} rows={2} />
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Historial de cobros -- drawer con reversión por fila. */}
            <Drawer
                title={historial ? `Historial de cobros — FAC ${historial.factura?.num_documento || ""}` : "Historial de cobros"}
                open={!!historial}
                onClose={() => setHistorial(null)}
                width={520}
            >
                {historial && (
                    <>
                        {historial.summary && (
                            <Row className="mb-3 g-2">
                                <Col xs={8}><small className="text-muted">Total</small><div>{money(historial.summary.total)}</div></Col>
                                <Col xs={8}><small className="text-muted">Cobrado</small><div>{money(historial.summary.cobrado)}</div></Col>
                                <Col xs={8}><small className="text-muted">Saldo</small><div><b>{money(historial.summary.saldo)}</b></div></Col>
                            </Row>
                        )}
                        {historialLoading ? (
                            <div className="text-center py-4">Cargando...</div>
                        ) : historial.cobros.length === 0 ? (
                            <Empty description="Sin cobros registrados" />
                        ) : (
                            historial.cobros.map((c) => (
                                <Card key={c.id} size="small" className="mb-2" style={{ opacity: c.estado === "REVERSADO" ? 0.6 : 1 }}>
                                    <Row className="align-items-center">
                                        <Col xs={16}>
                                            <div><b>{money(c.valor)}</b> · {c.metodo || "—"}</div>
                                            <div><small className="text-muted">{c.fecha_cobro ? format(new Date(c.fecha_cobro), "dd/MM/yyyy", { locale: es }) : "—"}</small></div>
                                            {c.referencia && <div><small>Ref: {c.referencia}</small></div>}
                                            {c.estado === "REVERSADO" && (
                                                <div><Tag color="red">Reversado</Tag>{c.motivo_reversion && <small className="text-muted"> — {c.motivo_reversion}</small>}</div>
                                            )}
                                        </Col>
                                        <Col xs={8} className="text-end">
                                            {c.estado === "ACTIVO" && hasPermission("billing.collections") && (
                                                <BotonKit
                                                    tamano="sm"
                                                    variante="destructivo"
                                                    icono={IconoRevertir}
                                                    cargando={reversando === c.id}
                                                    onClick={() => reversarCobro(c)}
                                                    data-testid={`cartera-reversar-${c.id}`}
                                                >
                                                    Reversar
                                                </BotonKit>
                                            )}
                                        </Col>
                                    </Row>
                                </Card>
                            ))
                        )}
                    </>
                )}
            </Drawer>
        </div>
    );
}

export default ReporteCartera;
